import aeI18n from 'sharedreact/utils/private/aeI18n';

import Immutable from 'immutable';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/es';
import 'moment/locale/it';
import 'moment/locale/de';
import 'moment/locale/ja';
import 'moment/locale/zh-cn';
import 'moment/locale/zh-tw';

import StringSupport from 'sharedreact/utils/StringSupport';

let _doTranslate: boolean = true;
let _fetchingQueue: {language: string, onSuccess: () => void}[] = [];
let _isFetching: boolean = false;

let _languageFiles: string[] = [];

function setLanguageBaseFiles (...languageFiles: string[]): void {
  _languageFiles = languageFiles;
}

function _doSetLanguage (language: string, onSuccess: () => void, onFailure: () => void) {
  language = language || 'en';

  let callbackStatus = { isCalled: false };

  setTimeout(()=> {
    if (!callbackStatus.isCalled) {
      onFailure();
    }
  }, 5000);

  aeI18n.initialize(
    _languageFiles,
    language.replace('-', '_'),
    () =>{
      callbackStatus.isCalled = true;
      onSuccess();
    }
  );
}

function setLanguage (language: string, onSuccess: () => void): void {
  _setLanguage(language, onSuccess, true);
}

function _setLanguage (language: string, onSuccess: () => void, retryOnce: boolean): void {
  _fetchingQueue.push({language, onSuccess});
  if (!_isFetching) {
    _isFetching = true;
    moment.locale(language);

    _doSetLanguage(language, () => {
      onSuccess();
      _processQueue(language);
    }, () => {
      if (retryOnce) {
        // fetching language files might have failed because the server
        // is not fully up and running, so retry once more
        _doSetLanguage(language, () => {
          onSuccess();
          _processQueue(language);
        }, () => {
          // do nothing, already retried once.
        });
      }
    });
  }
}

function _processQueue (language: string): void {
  _fetchingQueue.shift();
  _isFetching = false;

  while (_fetchingQueue.length > 0) {
    const nextTranslation: {
      language: string;
      onSuccess: () => void;
    } | undefined = _fetchingQueue.shift();

    if (nextTranslation != null) {
      if (nextTranslation.language !== language) {
        _setLanguage(nextTranslation.language, nextTranslation.onSuccess, false);
        break;
      } else {
        nextTranslation.onSuccess();
      }
    }
  }
}

function translate (
  stringToTranslate: string,
  ...rest: any /* add parameters as function arguments */
): string {
  return (_doTranslate ? aeI18n.translate(stringToTranslate, ...rest) : stringToTranslate);
}

function _tryToTranslate (stringToTranslate: string,
  ...rest: any): string {
  const translatedString: string = translate(stringToTranslate, ...rest);

  if (translatedString.startsWith('[') && translatedString.endsWith(']')) {
    return StringSupport.replaceArgs(stringToTranslate, ...rest);
  }
  return translatedString;
}

function tryToTranslate (stringToTranslate: string,
  ...rest: any): string {
    // first try to translate the arguments
  const args = [...rest].map((item) => (typeof item === 'string' ? _tryToTranslate(item) : item));
  return _tryToTranslate(stringToTranslate, ...args);
}

function tryToTranslateWithListArgs (stringToTranslate: string, args: Immutable.List<any>): string {
  if (args) {
    return tryToTranslate(stringToTranslate, ...args.toJS());
  }
  return tryToTranslate(stringToTranslate);
}

function smartTryToTranslate (stringToTranslate: string, prefix: string,
  ...rest: any): string {
  const translateTag: string = (prefix + stringToTranslate).replace(/[^a-zA-Z0-9.]/g, '').toLowerCase();
  const translatedString: string = translate(translateTag, ...rest);

  if (translatedString.startsWith('[') && translatedString.endsWith(']')) {
    return stringToTranslate;
  }
  return translatedString;
}

function getDisplayLanguage (language: string | null): string {
  switch (language) {
    case 'de': {
      return 'Deutsch';
    }
    case 'fr': {
      return 'Fran\u00E7ais';
    }
    case 'it': {
      return 'Italiano';
    }
    case 'es': {
      return 'Espa\u00F1ol';
    }
    case 'ja': {
      return '\u65E5\u672C\u8A9E';
    }
    case 'zh': {
      return '\u7B80\u4F53\u4E2D\u6587';
    }
    case 'zh-TW': {
      return '\u7E41\u9AD4\u4E2D\u6587';
    }
    case 'ko': {
      return '\uD55C\uAD6D\uC758';
    }
    case 'en':
    default: {
      return 'English';
    }
  }
}

function setDoTranslate (doTranslate: boolean): void {
  _doTranslate = doTranslate;
}

function _escapeRegexSpecialChars (input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function delocalizeDecimalSeparator (input: string): string {
  const decimalSeparator: string = _tryToTranslate('shared.general.decimalseparator');
  if (decimalSeparator !== '.' && decimalSeparator !== ',') {
    return input.replace(/,/g, '.').replace(new RegExp(_escapeRegexSpecialChars(decimalSeparator), 'g'), '.');
  }
  // replace all commas with dots
  return input.replace(/,/g, '.');
}

function localizeDecimalSeparator (input: string): string {
  const decimalSeparator: string = _tryToTranslate('shared.general.decimalseparator');
  if (decimalSeparator !== '.') {
    return input.replace(/\./g, decimalSeparator);
  } else {
    return input;
  }
}

function getBrowserLanguage (languageOptions: Immutable.List<string>): string | undefined {
  // get the browser languages:
  // - IE: the system's default language
  // - Firefox/Chrome: preferred content languages in browser settings
  const browserLanguages = window.navigator.languages || [
    window.navigator.language || (window.navigator as any)['userLanguage']
  ];

  // find the first language that is also available in the language options
  for (const lang of browserLanguages) {
    // first look for an exact match
    if (languageOptions.contains(lang)) {
      return lang;
    }

    // if no exact match was found, compare the first 2 letters of the languages
    const matchingLanguage = languageOptions.find((option: string) =>
      option.startsWith(lang.substring(0, 2))
    );
    if (matchingLanguage != null) {
      return matchingLanguage;
    }
  }

  // return undefined if none of the browser languages could be matched
  return undefined;
}

export {
  translate,
  tryToTranslate,
  tryToTranslateWithListArgs,
  smartTryToTranslate,
  getDisplayLanguage,
  delocalizeDecimalSeparator,
  localizeDecimalSeparator,
  getBrowserLanguage,
  setDoTranslate
};

export default {
  setLanguageBaseFiles,
  setLanguage,
  translate,
  tryToTranslate,
  smartTryToTranslate,
  tryToTranslateWithListArgs,
  getDisplayLanguage,
  delocalizeDecimalSeparator,
  localizeDecimalSeparator,
  getBrowserLanguage,
  setDoTranslate
};
